(() => {
  const globalKey = 'CyantHeaderCompactObserverInitialized';

  if (window[globalKey]) {
    window[globalKey].init(document);
    return;
  }

  const desktopMedia = window.matchMedia('(min-width: 990px)');
  const records = new Map();
  let mediaListenerAttached = false;

  const updateClass = (section, masthead) => {
    const mastheadPassed = masthead.getBoundingClientRect().bottom <= 0;
    section.classList.toggle('cyant-compact-visible', mastheadPassed);
  };

  const clearAnnouncement = (record) => {
    record.announcementObserver?.disconnect();
    record.announcementObserver = null;
    record.announcement = null;
    record.section.classList.remove('cyant-announcement-active');
    record.section.style.removeProperty('--cy-announcement-height');
  };

  const updateAnnouncementHeight = (record) => {
    if (!record.announcement || !desktopMedia.matches) return;
    const height = record.announcement.getBoundingClientRect().height;
    record.section.style.setProperty('--cy-announcement-height', `${height}px`);
    record.section.classList.toggle('cyant-announcement-active', height > 0);
  };

  const configureRecord = (record) => {
    record.observer?.disconnect();
    record.observer = null;
    clearAnnouncement(record);

    if (!desktopMedia.matches) {
      record.section.classList.remove('cyant-compact-visible');
      return;
    }

    let announcement = record.section.previousElementSibling;
    while (announcement && !announcement.matches('.announcement-bar-section')) {
      announcement = announcement.previousElementSibling;
    }
    if (announcement?.querySelector('.announcement-bar__message')) {
      record.announcement = announcement;
      updateAnnouncementHeight(record);
      if ('ResizeObserver' in window) {
        record.announcementObserver = new ResizeObserver(() => updateAnnouncementHeight(record));
        record.announcementObserver.observe(announcement);
      }
    }

    updateClass(record.section, record.masthead);

    if (!('IntersectionObserver' in window)) return;

    record.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const mastheadPassed = entry.boundingClientRect.bottom <= 0;
          record.section.classList.toggle('cyant-compact-visible', mastheadPassed);
        });
      },
      { threshold: 0 }
    );
    record.observer.observe(record.masthead);
  };

  const handleBreakpointChange = () => {
    records.forEach(configureRecord);
  };

  const attachMediaListener = () => {
    if (mediaListenerAttached) return;

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener('change', handleBreakpointChange);
    } else {
      desktopMedia.addListener(handleBreakpointChange);
    }
    mediaListenerAttached = true;
  };

  const detachMediaListener = () => {
    if (!mediaListenerAttached) return;

    if (desktopMedia.removeEventListener) {
      desktopMedia.removeEventListener('change', handleBreakpointChange);
    } else {
      desktopMedia.removeListener(handleBreakpointChange);
    }
    mediaListenerAttached = false;
  };

  const initSection = (section) => {
    const masthead = section.querySelector('.cyant-header--home .header');
    if (!masthead) return;

    const existingRecord = records.get(section);
    existingRecord?.observer?.disconnect();
    if (existingRecord) clearAnnouncement(existingRecord);

    const record = { section, masthead, observer: null, announcement: null, announcementObserver: null };
    records.set(section, record);
    attachMediaListener();
    configureRecord(record);
  };

  const destroySection = (section) => {
    const record = records.get(section);
    if (!record) return;

    record.observer?.disconnect();
    clearAnnouncement(record);
    section.classList.remove('cyant-compact-visible');
    records.delete(section);

    if (records.size === 0) detachMediaListener();
  };

  const init = (root = document) => {
    if (root instanceof Element && root.matches('.section-header')) initSection(root);
    root.querySelectorAll?.('.section-header').forEach(initSection);
  };

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    if (event.target.matches('.announcement-bar-section')) records.forEach(configureRecord);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    records.forEach((record, section) => {
      if (section === event.target || event.target.contains(section)) destroySection(section);
      else if (record.announcement && event.target.contains(record.announcement)) clearAnnouncement(record);
    });
  });

  document.addEventListener('shopify:section:reorder', () => records.forEach(configureRecord));

  window[globalKey] = { init };
  init(document);
})();
